import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback } from "react";

import { Mutations, Queries } from "@/constants";

export function usePendingInvitations() {
  const { data, loading, refetch } = useQuery(
    Queries.PENDING_INVITATIONS_QUERY,
    {
      fetchPolicy: "network-only",
    },
  );

  return {
    pendingInvitations: data?.onboardingStatus?.data?.pendingInvitations ?? [],
    loading,
    refetch,
  };
}

export function useAcceptInvitation() {
  const [mutate, { loading }] = useMutation(Mutations.ACCEPT_INVITATION);

  const accept = useCallback(
    async (accountIdentifier: string) => {
      await mutate({
        variables: {
          input: { accountIdentifier },
        },
        refetchQueries: ["User", "PendingInvitations", "Accounts"],
      });
    },
    [mutate],
  );

  return { accept, loading };
}

export function useDeclineInvitation() {
  const [mutate, { loading }] = useMutation(Mutations.DECLINE_INVITATION);

  const decline = useCallback(
    async (accountIdentifier: string) => {
      await mutate({
        variables: {
          input: { accountIdentifier },
        },
        refetchQueries: ["User", "PendingInvitations", "Accounts"],
      });
    },
    [mutate],
  );

  return { decline, loading };
}

/**
 * Take up the books someone shared with the person, for their firm - a
 * business of theirs they own: they hand them on from its Clients.
 */
export function useAcceptExternalGrant() {
  const [mutate, { loading }] = useMutation(Mutations.ACCEPT_EXTERNAL_GRANT);

  const accept = useCallback(
    async (externalGrantIdentifier: string, entityIdentifier: string) => {
      await mutate({
        variables: {
          input: { externalGrantIdentifier, entityIdentifier },
        },
        refetchQueries: ["ExternalGrants", "ExternalClients"],
      });
    },
    [mutate],
  );

  return { accept, loading };
}

/** Turn down the books someone shared with the person. */
export function useDeclineExternalGrant() {
  const [mutate, { loading }] = useMutation(Mutations.DECLINE_EXTERNAL_GRANT);

  const decline = useCallback(
    async (externalGrantIdentifier: string) => {
      await mutate({
        variables: {
          input: { externalGrantIdentifier },
        },
        refetchQueries: ["ExternalGrants"],
      });
    },
    [mutate],
  );

  return { decline, loading };
}
